import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { SuggestedCandidate, MatchReasons } from "@/types/candidateSuggestion";
import { ChevronRight, Info, Briefcase, MessageSquare } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AddInteractionDialog } from "@/components/candidates/interactions/AddInteractionDialog";
import { useShortlistCandidate } from "@/hooks/mutations/useShortlistCandidate";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface MatchingCandidateProps {
  suggestion: SuggestedCandidate;
  jobId: string;
  jobTitle: string;
  context?: "detail" | "preview";
}

export const MatchingCandidate = ({ suggestion, jobId, jobTitle, context = "detail" }: MatchingCandidateProps) => {
  const navigate = useNavigate();
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const { mutate: shortlistCandidateAction, isPending: isShortlisting } = useShortlistCandidate();
  const queryClient = useQueryClient();

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    return `${(firstName?.[0] || "").toUpperCase()}${(lastName?.[0] || "").toUpperCase()}`;
  };

  const getMatchColor = (percentage: number) => {
    if (percentage >= 90) return "bg-green-500/10 text-green-500 border-green-500/20";
    if (percentage >= 80) return "bg-emerald-500/10 text-emerald-500 border-emerald-500/20";
    if (percentage >= 70) return "bg-orange-500/10 text-orange-500 border-orange-500/20";
    return "bg-gray-500/10 text-gray-500 border-gray-500/20";
  };

  const formatMatchReasons = (reasons: MatchReasons) => {
    const parts: React.ReactNode[] = [];
    if (reasons.skills && reasons.skills.length > 0) {
      parts.push(<div key="skills"><strong>Skills:</strong> {reasons.skills.join(', ')}</div>);
    }
    if (reasons.experience) {
      parts.push(<div key="experience"><strong>Experience:</strong> {reasons.experience}</div>);
    }
    if (reasons.location) {
      parts.push(<div key="location"><strong>Location:</strong> {reasons.location}</div>);
    }
    if (reasons.status) {
      parts.push(<div key="status"><strong>Status:</strong> {reasons.status}</div>);
    }
    return parts.length > 0 ? parts : "No specific match reasons available.";
  };

  const displayName = suggestion.name || `${suggestion.first_name || ''} ${suggestion.last_name || ''}`.trim() || 'N/A';

  const handleNavigateToProfile = () => {
    navigate(`/candidates/${suggestion.id}`);
  };

  const handleShortlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!suggestion.id) {
      toast.error("Candidate ID is missing, cannot shortlist.");
      return;
    }
    shortlistCandidateAction({
      candidateId: suggestion.id,
      jobId: jobId,
      jobTitle: jobTitle,
      candidateName: displayName,
    });
  };

  const handleInitiateContact = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!suggestion.id) {
      toast.error("Candidate ID is missing, cannot initiate contact.");
      return;
    }
    setIsContactDialogOpen(true);
  };

  const isPreview = context === 'preview';

  return (
    <TooltipProvider delayDuration={300}>
      <div 
        className={cn(
          "flex items-center justify-between p-3 hover:bg-accent/50 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-primary/10 group",
          isPreview ? "p-2" : "p-4"
        )}
        onClick={handleNavigateToProfile}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Avatar className={cn(isPreview ? "h-10 w-10" : "h-12 w-12", "border-2 border-primary/5")}>
            <AvatarFallback className="bg-primary/10 text-xs">
              {getInitials(suggestion.first_name, suggestion.last_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className={cn("font-medium group-hover:text-primary transition-colors truncate", isPreview ? "text-sm" : "text-lg")}>
              {displayName}
            </div>
            <div className={cn("text-muted-foreground flex items-center gap-2 flex-wrap", isPreview ? "text-xs" : "text-sm")}>
              <span>{suggestion.experience_years ?? 'N/A'} yrs exp</span>
              {suggestion.skills && suggestion.skills.length > 0 && (
                <>
                  <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-muted-foreground"></span>
                  <span className="truncate">{suggestion.skills[0]}
                  {suggestion.skills.length > 1 && ` +${suggestion.skills.length - 1}`}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 ml-2">
          {!isPreview && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={handleShortlist} disabled={isShortlisting}>
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="end"><p>{isShortlisting ? "Shortlisting..." : "Shortlist for this job"}</p></TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8" onClick={handleInitiateContact}>
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="end"><p>Contact Candidate</p></TooltipContent>
              </Tooltip>
            </>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className={cn("h-6 w-6", isPreview ? "h-5 w-5" : "sm:h-8 sm:w-8")} onClick={(e) => e.stopPropagation()}>
                <Info className={cn(isPreview ? "h-3 w-3" : "h-4 w-4", "text-muted-foreground")} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="end" className="max-w-xs text-sm p-3" onClick={(e) => e.stopPropagation()}>
              <div className="space-y-1">{formatMatchReasons(suggestion.match_reasons)}</div>
            </TooltipContent>
          </Tooltip>
          <Badge variant="secondary" className={cn(getMatchColor(suggestion.match_score), isPreview ? "text-xs px-1.5 py-0.5" : "text-sm px-2 py-1")}>
            {suggestion.match_score}%
            {!isPreview && " Match"}
          </Badge>
          <ChevronRight className={cn("text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity", isPreview ? "h-3 w-3" : "h-4 w-4")} />
        </div>
      </div>
      {isContactDialogOpen && suggestion.id && (
        <AddInteractionDialog
          candidateId={suggestion.id}
          open={isContactDialogOpen}
          onOpenChange={(open) => {
            setIsContactDialogOpen(open);
          }}
          onInteractionAdded={() => {
            toast.success("Interaction logged successfully!");
            // Invalidate candidate interactions query to refetch
            if (suggestion.id) {
              queryClient.invalidateQueries({ queryKey: ['candidate-interactions', suggestion.id] });
            }
          }}
        />
      )}
    </TooltipProvider>
  );
};
